import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject, SetStateAction } from "react";
import { toast } from "sonner";
import { HANDOFF_DECLINED_UPDATE, HANDOFF_FAILED_UPDATE } from "@/hooks/voice/useHumanHandoff";
import { useVoiceBridge } from "@/hooks/voice/useVoiceBridge";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { HandoffAttemptLog, type HandoffTrigger } from "@/services/integrations/twilio/HandoffAttemptLog";
import { AgentPageProps } from "@/types/agent/agent-page";

interface VoiceAgentHandoffSurface {
  connected: boolean;
  getConversationId: () => string | null;
  sendContextualUpdate: (text: string) => void;
  endConversation: () => Promise<unknown>;
  onHumanHandoffRequested: (callback: () => void) => void;
}

/**
 * Live voice handoff to a human, via Twilio Voice JS SDK — fully additive,
 * dormant unless agent.voice_bridge_enabled is set (default false). Two
 * trigger paths (the AI client tool, wired here via onHumanHandoffRequested,
 * and the manual button rendered by AgentCore) both land in
 * handleConfirmHumanHandoff.
 *
 * The three deferred-wrap-up handoff refs (handoffInProgressRef etc.) are
 * created by the caller and passed in rather than owned here, because
 * useStudioConversationCore also needs them (its onConversationEnd checks
 * handoffInProgressRef before deciding whether to show the wrap-up) and is
 * called before this hook — voiceAgent, one of this hook's own inputs, only
 * exists once useStudioConversationCore has run.
 */
export function useHumanHandoffOrchestration({
  agent,
  voiceAgent,
  messageCount,
  shouldUseWrapUp,
  setShowWrapUp,
  handleRedirect,
  handoffInProgressRef,
  pendingRedirectUrlRef,
}: {
  agent: AgentPageProps["agentPageData"] | null;
  voiceAgent: VoiceAgentHandoffSurface;
  messageCount: number;
  shouldUseWrapUp: () => boolean;
  setShowWrapUp: (value: boolean) => void;
  handleRedirect: (redirectUrl: string) => void;
  handoffInProgressRef: MutableRefObject<boolean>;
  pendingRedirectUrlRef: MutableRefObject<string | null>;
}) {
  const [showHandoffPrompt, setPromptVisible] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const voiceBridge = useVoiceBridge();
  const attemptLogRef = useRef<HandoffAttemptLog | null>(null);
  const confirmedRef = useRef(false);
  const isVoiceBridgeEnabled = Boolean(agent?.voice_bridge_enabled);
  // The AI-driven trigger works off voice_bridge_enabled alone — clients can hide
  // the manual button (e.g. AI-only handoff) without disabling the capability.
  const showHumanHandoffButton = isVoiceBridgeEnabled && agent?.voice_bridge_show_button !== false;

  const beginAttempt = useCallback(
    (trigger: HandoffTrigger) => {
      if (!agent?.id) return null;
      const attempt = new HandoffAttemptLog({
        agentId: agent.id,
        conversationId: voiceAgent.getConversationId(),
        trigger,
      });
      attemptLogRef.current = attempt;
      return attempt;
    },
    [agent?.id, voiceAgent],
  );

  const showPromptFor = useCallback(
    (trigger: HandoffTrigger) => {
      confirmedRef.current = false;
      beginAttempt(trigger)?.record("prompt_shown");
      setPromptVisible(true);
    },
    [beginAttempt],
  );

  // The page opens and closes the prompt through this one setter, so it is
  // where the manual button and a dismissed prompt get logged.
  const setShowHandoffPrompt = useCallback(
    (next: SetStateAction<boolean>) => {
      const visible = typeof next === "function" ? next(showHandoffPrompt) : next;
      if (visible === showHandoffPrompt) return;
      if (visible) {
        showPromptFor("button");
        return;
      }
      setPromptVisible(false);
      attemptLogRef.current?.record("prompt_closed", "success", {
        confirmed: confirmedRef.current,
        bridge_status: voiceBridge.bridgeStatus,
      });
      // Closed without ever confirming: the Genie was told a prompt is on
      // screen, so it needs to hear the visitor walked away from it.
      if (!confirmedRef.current && voiceAgent.connected) {
        voiceAgent.sendContextualUpdate(HANDOFF_DECLINED_UPDATE);
      }
    },
    [showHandoffPrompt, showPromptFor, voiceBridge.bridgeStatus, voiceAgent],
  );

  useEffect(() => {
    if (!isVoiceBridgeEnabled) return;
    voiceAgent.onHumanHandoffRequested(() => showPromptFor("ai_tool"));
  }, [isVoiceBridgeEnabled, voiceAgent.onHumanHandoffRequested, showPromptFor]);

  const handleConfirmHumanHandoff = useCallback(async () => {
    if (!agent?.id) return;
    setHandoffError(null);
    confirmedRef.current = true;
    const attempt = attemptLogRef.current ?? beginAttempt("button");
    attempt?.record("confirmed", "success", { ai_session_connected: voiceAgent.connected });
    try {
      // The AI session is only torn down once the call is real — if the token,
      // the Twilio connection or the microphone fails, the visitor stays in
      // their existing conversation instead of being stranded with neither.
      const callAccepted = await voiceBridge.startBridge(
        agent.id,
        TranscriptStorageService.getHandoffMessages(voiceAgent.getConversationId()),
        attempt ?? undefined,
      );
      setPromptVisible(false);
      if (!callAccepted) return;
      if (voiceAgent.connected) {
        handoffInProgressRef.current = true;
        // Note why the session ended so the transcript/analysis reflects a
        // human handoff rather than reading as an ordinary hangup.
        voiceAgent.sendContextualUpdate(
          "The user requested to speak with a human and was connected to a live phone call. This conversation is ending now.",
        );
        await voiceAgent.endConversation();
        attempt?.record("ai_session_ended");
      }
    } catch (error) {
      console.error("[useHumanHandoffOrchestration] Failed to start human handoff:", error);
      setHandoffError(
        error instanceof Error ? error.message : "Couldn't connect you to a human. Please try again.",
      );
      if (voiceAgent.connected) voiceAgent.sendContextualUpdate(HANDOFF_FAILED_UPDATE);
    }
  }, [agent?.id, voiceBridge, voiceAgent, handoffInProgressRef, beginAttempt]);

  // Once the human call itself ends, show the wrap-up that was deferred when
  // the AI session ended for the handoff (see the conversation-core hook's
  // onConversationEnd, which checks handoffInProgressRef before deciding).
  useEffect(() => {
    if (!handoffInProgressRef.current) return;
    if (voiceBridge.bridgeStatus !== "ended" && voiceBridge.bridgeStatus !== "failed") return;
    handoffInProgressRef.current = false;
    if (pendingRedirectUrlRef.current) {
      const redirectUrl = pendingRedirectUrlRef.current;
      pendingRedirectUrlRef.current = null;
      handleRedirect(redirectUrl);
    } else if (shouldUseWrapUp() && messageCount > 0) {
      setShowWrapUp(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceBridge.bridgeStatus]);

  const handleHangupHumanHandoff = useCallback(() => {
    voiceBridge.endBridge();
  }, [voiceBridge]);

  // Mid-call failures (after the confirmation prompt has already closed) surface
  // as a toast instead of reopening the prompt, since the AI session is already gone.
  useEffect(() => {
    if (voiceBridge.bridgeStatus === "failed" && voiceBridge.bridgeError && !showHandoffPrompt) {
      toast.error(voiceBridge.bridgeError.message || "The call couldn't connect.");
    }
  }, [voiceBridge.bridgeStatus, voiceBridge.bridgeError, showHandoffPrompt]);

  return {
    voiceBridge,
    isVoiceBridgeEnabled,
    showHumanHandoffButton,
    showHandoffPrompt,
    setShowHandoffPrompt,
    handoffError,
    setHandoffError,
    handoffInProgressRef,
    pendingRedirectUrlRef,
    handleConfirmHumanHandoff,
    handleHangupHumanHandoff,
  };
}
